import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────────

export type TipoAvaliacao = 'PROVA' | 'TRABALHO' | 'ATIVIDADE' | 'PARTICIPACAO' | 'RECUPERACAO';
export type StatusAvaliacao = 'RASCUNHO' | 'AGENDADA' | 'EM_APLICACAO' | 'CONCLUIDA' | 'CANCELADA';

export interface Avaliacao {
  id: string;
  escolaId: string;
  turmaId: string;
  materiaId: string;
  professorId: string;
  titulo: string;
  descricao?: string;
  tipo: TipoAvaliacao;
  dataAplicacao?: string;
  peso: number;
  valorMaximo: number;
  bimestre: number;
  anoLetivo: number;
  status: StatusAvaliacao;
  criadoEm: string;
  turma?: { id: string; nome: string };
  materia?: { id: string; nome: string };
  professor?: { id: string; nome: string };
  _count?: { notas: number };
}

export interface NotaAluno {
  id: string;
  avaliacaoId: string;
  alunoId: string;
  professorId: string;
  valorObtido?: number;
  conceito?: string;
  observacao?: string;
  entregou: boolean;
  faltou: boolean;
  aluno?: { id: string; nome: string; matricula?: string };
  avaliacao?: {
    id: string;
    titulo: string;
    tipo: TipoAvaliacao;
    peso: number;
    valorMaximo: number;
    bimestre: number;
    anoLetivo: number;
    materia?: { id: string; nome: string };
  };
}

export interface CreateAvaliacaoInput {
  escolaId: string;
  turmaId: string;
  materiaId: string;
  professorId: string;
  titulo: string;
  descricao?: string;
  tipo: TipoAvaliacao;
  dataAplicacao?: string;
  peso?: number;
  valorMaximo?: number;
  bimestre: number;
  anoLetivo: number;
}

export interface NotaItemInput {
  alunoId: string;
  valorObtido?: number;
  conceito?: string;
  observacao?: string;
  entregou?: boolean;
  faltou?: boolean;
}

export interface NotaHistorico {
  id: string;
  avaliacaoId: string;
  alunoId: string;
  acao: 'CREATE' | 'UPDATE';
  valorAnterior: number | null;
  valorNovo: number | null;
  conceitoAnterior?: string | null;
  conceitoNovo?: string | null;
  faltouAnterior?: boolean | null;
  faltouNovo?: boolean | null;
  entregouAnterior?: boolean | null;
  entregouNovo?: boolean | null;
  criadoEm: string;
  aluno?: { id: string; nome: string };
  alteradoPor?: { id: string; nome: string };
}

export interface MediaMateria {
  materiaId: string;
  materiaNome: string;
  totalAvaliacoes: number;
  mediaGeral: number;
  maiorNota: number;
  menorNota: number;
}

// ── Hook ─────────────────────────────────────────────────────────────────

const BASE = '/avaliacoes';
const QK = 'avaliacoes';

/**
 * Hook para gerenciar Avaliações + Notas (Fase 3).
 *
 * @param filtros - Filtros para listar avaliações
 */
export function useAvaliacoes(filtros?: {
  turmaId?: string;
  materiaId?: string;
  professorId?: string;
  bimestre?: number;
  anoLetivo?: number;
  status?: StatusAvaliacao;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QK, filtros],
    queryFn: async () => {
      const { data } = await api.get<Avaliacao[]>(BASE, { params: filtros });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!(filtros?.turmaId || filtros?.professorId),
  });

  const criar = useMutation({
    mutationFn: (input: CreateAvaliacaoInput) => api.post(BASE, input).then(r => r.data as Avaliacao),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, ...input }: Partial<Avaliacao> & { id: string }) =>
      api.patch(`${BASE}/${id}`, input).then(r => r.data as Avaliacao),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const mudarStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusAvaliacao }) =>
      api.patch(`${BASE}/${id}/status`, { status }).then(r => r.data as Avaliacao),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  /** Lançar notas em lote para uma avaliação */
  const lancarNotas = useMutation({
    mutationFn: ({ avaliacaoId, professorId, notas }: {
      avaliacaoId: string;
      professorId: string;
      notas: NotaItemInput[];
    }) => api.post(`${BASE}/${avaliacaoId}/notas`, { professorId, notas }).then(r => r.data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [QK] });
      queryClient.invalidateQueries({ queryKey: ['notas'] });
      queryClient.invalidateQueries({ queryKey: ['notas-historico', vars.avaliacaoId] });
    },
  });

  /** Buscar notas de uma avaliação específica */
  const useNotasAvaliacao = (avaliacaoId: string | undefined) =>
    useQuery({
      queryKey: ['notas', 'avaliacao', avaliacaoId],
      queryFn: async () => {
        if (!avaliacaoId) return [];
        const { data } = await api.get<NotaAluno[]>(`${BASE}/${avaliacaoId}/notas`);
        return Array.isArray(data) ? data : [];
      },
      enabled: !!avaliacaoId,
    });

  /** Buscar notas de um aluno (para ficha) */
  const useNotasAluno = (alunoId: string | undefined, filtrosNota?: { bimestre?: number; anoLetivo?: number }) =>
    useQuery({
      queryKey: ['notas', 'aluno', alunoId, filtrosNota],
      queryFn: async () => {
        if (!alunoId) return [];
        const { data } = await api.get<NotaAluno[]>(`${BASE}/alunos/${alunoId}/notas`, { params: filtrosNota });
        return Array.isArray(data) ? data : [];
      },
      enabled: !!alunoId,
    });

  /** Histórico de alterações de notas (auditoria) */
  const useHistoricoNotas = (avaliacaoId: string | undefined) =>
    useQuery({
      queryKey: ['notas-historico', avaliacaoId],
      queryFn: async () => {
        if (!avaliacaoId) return [];
        const { data } = await api.get<NotaHistorico[]>(
          `${BASE}/${avaliacaoId}/historico-notas`,
        );
        return Array.isArray(data) ? data : [];
      },
      enabled: !!avaliacaoId,
      staleTime: 30_000,
    });

  /** Buscar médias de uma turma */
  const useMediasTurma = (turmaId: string | undefined, filtrosMedia?: { materiaId?: string; bimestre?: number; anoLetivo?: number }) =>
    useQuery({
      queryKey: ['medias', 'turma', turmaId, filtrosMedia],
      queryFn: async () => {
        if (!turmaId) return [];
        const { data } = await api.get<MediaMateria[]>(`${BASE}/turmas/${turmaId}/medias`, { params: filtrosMedia });
        return Array.isArray(data) ? data : [];
      },
      enabled: !!turmaId,
    });

  return {
    avaliacoes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    criar,
    atualizar,
    mudarStatus,
    lancarNotas,
    useNotasAvaliacao,
    useHistoricoNotas,
    useNotasAluno,
    useMediasTurma,
  };
}
